import { cn } from "@/lib/utils";

/**
 * Sabzy brand logo.
 *
 * The mark is a custom SVG combining:
 *  - A rounded square base in copper-amber gradient
 *  - A typographic ‘S’ formed by two precise arcs (no plain font letter)
 *  - A tiny spark dot — the "AI" signal
 *
 * Inspired by the visual rigor of OpenAI / Linear / Anthropic marks but
 * distinct: speed (the arcs imply motion) + warmth (copper) + craft.
 */
export function Logo({ size = 32, className, withText = true, textClassName }) {
  return (
    <div className={cn("flex items-center gap-2.5", className)} data-testid="sabzy-logo">
      <LogoMark size={size} />
      {withText && (
        <div className={cn("flex items-baseline leading-none", textClassName)}>
          <span
            className="text-[17px] font-semibold tracking-[-0.025em] text-foreground"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            sabzy
          </span>
          <span
            className="text-[17px] font-semibold tracking-[-0.025em] text-primary"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            .ai
          </span>
        </div>
      )}
    </div>
  );
}

/** Just the mark — useful for avatars / favicons / standalone uses */
export function LogoMark({ size = 32, className }) {
  // Use a UID per instance so multiple logos on a page don't collide on gradient IDs.
  const uid = "sabzy-" + Math.random().toString(36).slice(2, 8);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("shrink-0", className)}
      aria-label="Sabzy"
    >
      <defs>
        <linearGradient id={`${uid}-bg`} x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="hsl(28 100% 62%)" />
          <stop offset="0.55" stopColor="hsl(22 92% 50%)" />
          <stop offset="1" stopColor="hsl(16 85% 38%)" />
        </linearGradient>
        <linearGradient id={`${uid}-s`} x1="10" y1="8" x2="30" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="1" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0.88" />
        </linearGradient>
        <linearGradient id={`${uid}-shine`} x1="0" y1="0" x2="40" y2="14" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.22" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Rounded square base */}
      <rect width="40" height="40" rx="10" fill={`url(#${uid}-bg)`} />

      {/* Top glass highlight */}
      <rect width="40" height="14" rx="10" fill={`url(#${uid}-shine)`} />

      {/* Custom typographic "S" — built from two cubic arcs forming an elegant ribbon */}
      <path
        d="M27.4 13.6c-1.5-2.1-4.1-3.2-7.4-3.2-4.5 0-7.6 2.4-7.6 5.7 0 3.2 2.4 4.7 6.6 5.5l2.4.5c2.5.5 3.6 1.1 3.6 2.4 0 1.5-1.5 2.4-4 2.4-2.8 0-4.6-1-5.7-2.9l-3 2c1.5 2.6 4.4 4 8.6 4 4.9 0 8.1-2.3 8.1-5.8 0-3-1.9-4.6-6.4-5.5l-2.5-.5c-2.5-.5-3.7-1.1-3.7-2.4 0-1.4 1.4-2.3 3.8-2.3 2.4 0 4 .9 4.9 2.5l2.3-2.4z"
        fill={`url(#${uid}-s)`}
      />

      {/* Tiny spark dot — the "AI" signal */}
      <circle cx="32" cy="8" r="2" fill="#FFFFFF" opacity="0.95" />
      <circle cx="32" cy="8" r="3.5" fill="#FFFFFF" opacity="0.18" />

      {/* Subtle inner border for crispness on light bg */}
      <rect x="0.5" y="0.5" width="39" height="39" rx="9.5" stroke="black" strokeOpacity="0.06" fill="none" />
    </svg>
  );
}
